// Utility helper functions
import { HotelContractModel, SeasonModel, SeasonDateRangeModel } from "../models";

/**
 * Normalize font size for consistent display across different screen sizes
 * @param size - The base font size in pixels
 * @returns The normalized font size
 */
export const normalizeFont = (size: number): number => {
    return size;
};

/**
 * Combine multiple styles into a single style object
 * @param styles - Array of style objects to combine
 * @returns Combined style object
 */
export const combineStyles = (...styles: React.CSSProperties[]): React.CSSProperties => {
    return Object.assign({}, ...styles);
};

/**
 * Create conditional styles based on a condition
 * @param condition - The condition to check
 * @param trueStyle - Style to apply if condition is true
 * @param falseStyle - Style to apply if condition is false
 * @returns The appropriate style object
 */
export const conditionalStyle = (
    condition: boolean,
    trueStyle: React.CSSProperties,
    falseStyle: React.CSSProperties = {}
): React.CSSProperties => {
    return condition ? trueStyle : falseStyle;
};

/**
 * Validates an optional website URL. Empty values are allowed.
 * Accepts http(s):// URLs and bare domains (e.g. www.example.com).
 */
export const validateWebsiteUrl = (value: string): string | undefined => {
    if (!value || value.trim() === '') {
        return undefined;
    }

    const trimmed = value.trim();

    try {
        const urlToTest = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        const url = new URL(urlToTest);

        if (!url.hostname || !url.hostname.includes('.')) {
            return 'Please enter a valid website URL';
        }

        return undefined;
    } catch {
        return 'Please enter a valid website URL';
    }
};

/**
 * Normalizes a website URL for storage (trim + prepend https:// when missing).
 * Returns null for empty input.
 */
export const normalizeWebsiteUrl = (value: string | null | undefined): string | null => {
    if (!value || value.trim() === '') {
        return null;
    }

    const trimmed = value.trim();
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Interface for the missing date range result
 */
export interface MissingDateRange {
    start: string;
    end: string;
}

/**
 * Interface for internal season range processing
 */
interface SeasonRangeProcessed {
    start: Date;
    end: Date;
}

/**
 * Calculate missing date ranges (gaps) in a hotel contract
 * that are not covered by any season
 *
 * @param data - The hotel contract data containing seasons and date ranges
 * @returns Array of missing date ranges with start and end dates in ISO format (YYYY-MM-DD)
 */
export const getMissingDateRanges = (data: HotelContractModel): MissingDateRange[] => {
    // Validate input
    if (!data.contract_start_date || !data.contract_end_date) {
        return [];
    }

    const contractStart: Date = new Date(data.contract_start_date);
    const contractEnd: Date = new Date(data.contract_end_date);

    // Collect & sort all season ranges
    const seasonRanges: SeasonRangeProcessed[] = data.season
        .filter((s: SeasonModel) => s.flag_remaining_season === false)
        .flatMap((s: SeasonModel) => s.season_date_range || [])
        .filter((r: SeasonDateRangeModel) => r.season_start_date && r.season_end_date)
        .map((r: SeasonDateRangeModel) => ({
            start: new Date(r.season_start_date),
            end: new Date(r.season_end_date)
        }))
        .sort((a: SeasonRangeProcessed, b: SeasonRangeProcessed) => a.start.getTime() - b.start.getTime());

    const result: MissingDateRange[] = [];
    let currentStart: Date = new Date(contractStart);

    for (const range of seasonRanges) {
        // If there is a gap before this season
        if (currentStart < range.start) {
            const gapEnd: Date = new Date(range.start);
            gapEnd.setDate(gapEnd.getDate() - 1);

            if (currentStart <= gapEnd) {
                result.push({
                    start: currentStart.toISOString().split("T")[0],
                    end: gapEnd.toISOString().split("T")[0]
                });
            }
        }

        // Move currentStart to after this season
        if (currentStart <= range.end) {
            currentStart = new Date(range.end);
            currentStart.setDate(currentStart.getDate() + 1);
        }
    }

    // Final gap after last season
    if (currentStart <= contractEnd) {
        result.push({
            start: currentStart.toISOString().split("T")[0],
            end: contractEnd.toISOString().split("T")[0]
        });
    }

    return result;
};

export const validateEmailHelper = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};